<script setup>
import { useScrollLock } from '@/composables/useScrollLock';

/**
 * 작명 세부 정보 입력 — 필드 배열 기반 통합 팝업.
 * 16개 모든 작명 모드를 처리 (한글이름추천의 돌림자/추천개수 포함).
 *
 * fields 배열 항목 형식:
 *   - { type: 'text', key, label, required?, showAsterisk?, placeholder? }
 *   - { type: 'buttons', key, label, required?, showAsterisk?, options: [{key, label}] }
 *   - { type: 'check-buttons', key, label, required?, showAsterisk?, options: [{key, label}] }
 *     → buttons와 같지만 활성 시 좌측에 체크 아이콘 표시 (큰 버튼, 추천 개수 등)
 *   - { type: 'radio-text', label, required?, showAsterisk?, radioKey, textKey, options, placeholder? }
 *     → 라디오 그룹 + 텍스트 인풋 (돌림자처럼 위치 선택 + 자유 입력)
 *
 * showAsterisk가 명시되지 않으면 required 값을 따라간다.
 * (예: 라벨에 '(선택)' 문구가 들어있고 시각적으로만 asterisk를 숨기고 싶으면 required:true + showAsterisk:false)
 */
const props = defineProps({
  isVisible: { type: Boolean, default: false },
  initialData: { type: Object, default: null },
  fields: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'submit']);

/** 팝업 노출 중 부모 스크롤 잠금 (PC/모바일 자동 분기) */
useScrollLock(computed(() => props.isVisible));

const values = ref({});

const initValues = () => {
  const init = props.initialData || {};
  const next = {};
  for (const f of props.fields) {
    if (f.type === 'text') {
      next[f.key] = init[f.key] ?? '';
    } else if (f.type === 'buttons' || f.type === 'check-buttons') {
      next[f.key] = init[f.key] ?? null;
    } else if (f.type === 'radio-text') {
      next[f.radioKey] = init[f.radioKey] ?? null;
      next[f.textKey] = init[f.textKey] ?? '';
    }
  }
  values.value = next;
};

/** charset 프리셋 — 입력값이 있을 때만 검사 (선택 필드 포함). 미통과 시 제출 차단 + 안내 문구.
 *  IME 한글 조합 중간값(자모)을 깨뜨리지 않도록 입력 자체는 막지 않고 제출 단계에서만 검증한다. */
const CHARSET_RULES = {
  korean: { pattern: /^[가-힣]+$/, message: '한글만 입력할 수 있어요.' },
  'korean-multi': { pattern: /^[가-힣]+(?:[,\s·]+[가-힣]+)*$/, message: '한글만 입력할 수 있어요. (여러 글자는 쉼표로 구분)' },
  // CJK 통합 한자(기본 U+4E00-9FFF + 확장A U+3400-4DBF) + 호환 한자(U+F900-FAFF)
  hanja: { pattern: /^[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]+$/, message: '한자만 입력할 수 있어요.' },
};

const charsetError = (f) => {
  const rule = CHARSET_RULES[f.charset];
  if (!rule) return '';
  const key = f.type === 'radio-text' ? f.textKey : f.key;
  const v = String(values.value[key] ?? '').trim();
  if (!v) return '';
  return rule.pattern.test(v) ? '' : rule.message;
};

const canSubmit = computed(() => {
  for (const f of props.fields) {
    // 문자 종류 위반은 required 여부와 무관하게 차단 (값이 있을 때만 검사)
    if ((f.type === 'text' || f.type === 'radio-text') && charsetError(f)) return false;
    if (!f.required) continue;
    if (f.type === 'text' && !String(values.value[f.key] ?? '').trim()) return false;
    if ((f.type === 'buttons' || f.type === 'check-buttons')
        && (values.value[f.key] === null || values.value[f.key] === undefined)) return false;
    if (f.type === 'radio-text'
        && (values.value[f.radioKey] === null || values.value[f.radioKey] === undefined)) return false;
  }
  return true;
});

const showAsterisk = (f) => f.showAsterisk ?? f.required;

/** 라디오 토글 — 이미 선택된 키를 다시 누르면 해제 */
const toggleRadio = (field, key) => {
  values.value[field.radioKey] = values.value[field.radioKey] === key ? null : key;
};

const close = () => emit('close');

const submit = () => {
  if (!canSubmit.value) return;
  // 텍스트 값은 trim해서 전달 — 앞뒤 공백이 그대로 유료 분석 입력으로 가지 않도록
  const out = { ...values.value };
  for (const f of props.fields) {
    if (f.type === 'text') out[f.key] = String(out[f.key] ?? '').trim();
    else if (f.type === 'radio-text') out[f.textKey] = String(out[f.textKey] ?? '').trim();
  }
  emit('submit', out);
};

watch(() => props.isVisible, (v) => {
  if (v) initValues();
});
</script>

<template>
  <div v-if="isVisible" class="pop-naming-detail-backdrop" @click.self="close">
    <div class="pop-naming-detail">
      <div class="pop-naming-detail__header">
        <h3 class="pop-naming-detail__title">세부 정보 입력</h3>
        <button type="button" class="pop-naming-detail__close" @click="close" aria-label="닫기">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="#333"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>

      <div class="pop-naming-detail__body">
        <div
          v-for="f in fields"
          :key="f.key"
          class="pop-naming-detail__field"
        >
          <label class="pop-naming-detail__label">
            {{ f.label }}<span v-if="showAsterisk(f)" class="pop-naming-detail__required"> *</span>
          </label>

          <template v-if="f.type === 'text'">
            <input
              :value="values[f.key]"
              type="text"
              class="pop-naming-detail__input"
              :placeholder="f.placeholder || ''"
              :maxlength="f.maxLength || 30"
              @input="values[f.key] = $event.target.value"
            />
            <p v-if="charsetError(f)" class="pop-naming-detail__field-error">{{ charsetError(f) }}</p>
          </template>

          <div v-else-if="f.type === 'buttons'" class="pop-naming-detail__row">
            <button
              v-for="o in f.options"
              :key="o.key"
              type="button"
              class="pop-naming-detail__btn"
              :class="{ active: values[f.key] === o.key }"
              @click="values[f.key] = o.key"
            >
              {{ o.label }}
            </button>
          </div>

          <div v-else-if="f.type === 'check-buttons'" class="pop-naming-detail__row">
            <button
              v-for="o in f.options"
              :key="o.key"
              type="button"
              class="pop-naming-detail__btn pop-naming-detail__btn--lg"
              :class="{ active: values[f.key] === o.key }"
              @click="values[f.key] = o.key"
            >
              <svg
                class="pop-naming-detail__btn-check"
                :class="{ 'is-hidden': values[f.key] !== o.key }"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                  fill="#724CE5"
                />
              </svg>
              <span class="pop-naming-detail__btn-label">{{ o.label }}</span>
            </button>
          </div>

          <template v-else-if="f.type === 'radio-text'">
            <div class="pop-naming-detail__radios">
              <button
                v-for="o in f.options"
                :key="o.key"
                type="button"
                class="pop-naming-detail__radio"
                @click="toggleRadio(f, o.key)"
              >
                <span
                  class="pop-naming-detail__radio-mark"
                  :class="{ checked: values[f.radioKey] === o.key }"
                ></span>
                <span class="pop-naming-detail__radio-label">{{ o.label }}</span>
              </button>
            </div>
            <input
              :value="values[f.textKey]"
              type="text"
              class="pop-naming-detail__input"
              :placeholder="f.placeholder || ''"
              :maxlength="f.maxLength || 30"
              @input="values[f.textKey] = $event.target.value"
            />
            <p v-if="charsetError(f)" class="pop-naming-detail__field-error">{{ charsetError(f) }}</p>
          </template>
        </div>
      </div>

      <button
        type="button"
        class="pop-naming-detail__submit"
        :class="{ enabled: canSubmit }"
        :disabled="!canSubmit"
        @click="submit"
      >
        작성 완료
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/nangman_style/_analysis_main_color.scss';

$naming-accent-bright: #724ce5;

.pop-naming-detail-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 9999;
  box-sizing: border-box;
}

.pop-naming-detail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 400px;
  max-height: calc(100vh - 32px);
  padding: 15px 24px 20px;
  border-radius: 16px;
  background: #fff;
  box-sizing: border-box;
  overflow-y: auto;
}

/* 모바일: 스크롤바 숨김 */
@media (max-width: 768px) {
  .pop-naming-detail {
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.pop-naming-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 10px;
}

.pop-naming-detail__title {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
}

.pop-naming-detail__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.pop-naming-detail__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding-bottom: 15px;
}

.pop-naming-detail__field {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.pop-naming-detail__label {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
}

.pop-naming-detail__required {
  color: #eb4c10;
}

.pop-naming-detail__input {
  width: 100%;
  height: 48px;
  padding: 14px 16px;
  border-radius: 6px;
  border: 1px solid #ebebeb;
  background: #fff;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  box-sizing: border-box;
  outline: none;

  /* iOS Safari: input focus 시 16px 미만이면 자동 확대됨 — 모바일에서만 16px로 올림 */
  @media (max-width: 768px) {
    font-size: 16px;
  }

  &::placeholder {
    color: #757575;
  }

  &:focus {
    border-color: $naming-accent-bright;
  }
}

.pop-naming-detail__field-error {
  margin: 6px 2px 0;
  color: #e65555;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
}

.pop-naming-detail__row {
  display: flex;
  gap: 10px;
  width: 100%;
}

.pop-naming-detail__btn {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 40px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid #ebebeb;
  background: #fff;
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;

  /* --lg: check-buttons 전용. 모바일 3칸 grid([체크 자리 | 글자(정중앙) | 대칭 빈 자리])로
     글자 정중앙 유지 + 체크는 visibility 토글. PC는 absolute로 글자 옆 부착 */
  &--lg {
    display: grid;
    grid-template-columns: 24px 1fr 24px;
    align-items: center;
    gap: 4px;
    height: 48px;
    padding: 0 8px;
  }

  &.active {
    border-color: $naming-accent-bright;
    color: $naming-accent-bright;
    background: #f8f6fd;
    font-weight: 600;
  }
}

.pop-naming-detail__btn-check {
  grid-column: 1;

  &.is-hidden {
    visibility: hidden;
  }
}

.pop-naming-detail__btn-label {
  grid-column: 2;
  text-align: center;
}

@media (min-width: 769px) {
  .pop-naming-detail__btn--lg {
    position: relative;
    display: flex;
    gap: 0;
    padding: 0 16px;
  }

  .pop-naming-detail__btn--lg .pop-naming-detail__btn-check {
    position: absolute;
    left: 37px;
    top: 50%;
    transform: translateY(-50%);
  }

  .pop-naming-detail__btn--lg .pop-naming-detail__btn-label {
    text-align: center;
  }
}

/* radio-text 전용 — 라디오 그룹 + 텍스트 인풋 (돌림자 등) */
.pop-naming-detail__radios {
  display: flex;
  gap: 15px;
  align-items: center;
}

.pop-naming-detail__radio {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.pop-naming-detail__radio-mark {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 4px solid #d9d9d9;
  background: #fff;
  flex-shrink: 0;
  box-sizing: border-box;

  &.checked {
    border-color: $naming-accent-bright;
  }
}

.pop-naming-detail__radio-label {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
}

.pop-naming-detail__submit {
  width: 100%;
  padding: 14px 20px;
  border-radius: 6px;
  border: none;
  background: #c2c2c2;
  color: #fff;
  font-family: Pretendard;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  cursor: not-allowed;
  box-sizing: border-box;

  &.enabled {
    background: #eb4c10;
    cursor: pointer;
  }
}
</style>
